//frontend/src/components/admin/DeliveryStaffList.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
    User,
    Plus,
    Edit,
    Trash2,
    RefreshCw,
    Eye,
    Lock,
    Unlock,
    Search,
    ChevronLeft,
    ChevronRight,
    ArrowLeft
} from 'lucide-react';

const DeliveryStaffList = () => {
    const [staffList, setStaffList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const navigate = useNavigate();
    const location = useLocation();

    // Always fetch fresh data when component mounts or when URL has a timestamp
    useEffect(() => {
        fetchDeliveryStaff();
    }, [location.search]); // Re-fetch when URL search params change

    const fetchDeliveryStaff = async () => {
        try {
            setLoading(true);
            // Add a cache-busting parameter to prevent getting cached responses
            const timestamp = new Date().getTime();
            const { data } = await axios.get(`/api/v1/admin/delivery-staff?t=${timestamp}`, {
                withCredentials: true
            });

            if (data.success) {
                setStaffList(data.deliveryStaff || []);
            } else {
                toast.error('Failed to fetch delivery staff');
            }
        } catch (error) {
            console.error('Error fetching delivery staff:', error);
            toast.error(error.response?.data?.message || 'Error fetching delivery staff');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleBlock = async (id, isCurrentlyBlocked) => {
        try {
            const { data } = await axios.put(
                `/api/v1/admin/delivery-staff/${id}/toggle-block`,
                {},
                { withCredentials: true }
            );

            if (data.success) {
                toast.success(`Staff ${isCurrentlyBlocked ? 'unblocked' : 'blocked'} successfully`);
                // Update the local state by re-fetching
                fetchDeliveryStaff();
            } else {
                toast.error(data.message || 'Error toggling staff status');
            }
        } catch (error) {
            console.error('Error toggling staff status:', error);
            toast.error(error.response?.data?.message || 'Error toggling staff status');
        }
    };

    const handleDelete = async (id, name) => {
        if (!window.confirm(`Are you sure you want to delete ${name}?`)) {
            return;
        }

        try {
            const { data } = await axios.delete(`/api/v1/admin/delivery-staff/${id}`, {
                withCredentials: true
            });

            if (data.success) {
                toast.success('Delivery staff deleted successfully');
                // Update the list by removing the deleted staff member
                fetchDeliveryStaff();
            } else {
                toast.error(data.message || 'Error deleting delivery staff');
            }
        } catch (error) {
            console.error('Error deleting delivery staff:', error);
            toast.error(error.response?.data?.message || 'Error deleting delivery staff');
        }
    };

    // Filter staff based on search term
    const filteredStaff = staffList.filter(staff => 
        staff.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        staff.loginId.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Pagination logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredStaff.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredStaff.length / itemsPerPage);

    // Reset to first page when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    // Refresh button handler
    const handleRefresh = () => {
        fetchDeliveryStaff();
        toast.info('Refreshing delivery staff list');
    };

    if (loading && staffList.length === 0) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <RefreshCw className="h-10 w-10 text-[#dc2626] animate-spin" />
                <span className="ml-2 text-base">Loading delivery staff...</span>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-3 py-6">
            {/* Back to Dashboard Button - Right aligned */}
            <div className="flex justify-end mb-4">
                <button
                    onClick={() => navigate('/admin/dashboard')}
                    className="flex items-center gap-1.5 text-gray-600 hover:text-[#dc2626] transition-colors text-sm"
                >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back to Dashboard</span>
                </button>
            </div>

            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="p-4 border-b border-gray-200">
                    <div className="flex flex-col md:flex-row justify-between items-center mb-4">
                        <h1 className="text-xl font-bold text-[#dc2626] mb-4 md:mb-0 font-['Crimson_Text']">
                            Delivery Staff Management
                        </h1>
                        <div className="flex gap-3">
                            <button
                                onClick={handleRefresh}
                                className="inline-flex items-center px-3 py-1.5 text-sm bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                            >
                                <RefreshCw className="h-4 w-4 mr-1.5" />
                                Refresh
                            </button>
                            <Link
                                to="/admin/delivery-staff/new"
                                className="inline-flex items-center px-3 py-1.5 text-sm bg-[#dc2626] text-white rounded-lg hover:bg-[#dc2626]/90 transition-colors"
                            >
                                <Plus className="h-4 w-4 mr-1.5" />
                                Add New Staff
                            </Link>
                        </div>
                    </div>

                    {/* Search and filters */}
                    <div className="relative mt-3">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="h-4 w-4 text-gray-400" />
                        </div>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="block w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:border-[#dc2626] focus:ring-[#dc2626]"
                            placeholder="Search by name or login ID"
                        />
                    </div>
                </div>

                {/* Desktop Staff Table */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Staff Member
                                </th>
                                <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Contact
                                </th>
                                <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Service Areas
                                </th>
                                <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {currentItems.length > 0 ? (
                                currentItems.map((staff) => (
                                    <tr key={staff._id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="h-8 w-8 flex-shrink-0">
                                                    {staff.profilePicture ? (
                                                        <img
                                                            className="h-8 w-8 rounded-full object-cover"
                                                            src={`${staff.profilePicture}?t=${new Date().getTime()}`}
                                                            alt={staff.name}
                                                            onError={(e) => {
                                                                e.target.onerror = null;
                                                                e.target.src = "/images/default_avatar.png";
                                                            }}
                                                        />
                                                    ) : (
                                                        <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                                                            <User className="h-5 w-5 text-gray-400" />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="ml-3">
                                                    <div className="text-xs font-medium text-gray-900">{staff.name}</div>
                                                    <div className="text-xs text-gray-500">Login ID: {staff.loginId}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="text-xs text-gray-900">{staff.phone || 'N/A'}</div>
                                            <div className="text-xs text-gray-500">{staff.email || 'No email provided'}</div>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                staff.isBlocked
                                                    ? 'bg-red-100 text-red-800'
                                                    : 'bg-green-100 text-green-800'
                                            }`}>
                                                {staff.isBlocked ? 'Blocked' : 'Active'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-wrap gap-1">
                                                {staff.assignedPincodes && staff.assignedPincodes.length > 0 ? (
                                                    staff.assignedPincodes.map((pincode, index) => (
                                                        <span
                                                            key={`${staff._id}-${pincode}-${index}`}
                                                            className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                                                        >
                                                            {pincode}
                                                        </span>
                                                    ))
                                                ) : (
                                                    <span className="text-xs text-gray-500">No areas assigned</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-right text-xs font-medium">
                                            <div className="flex gap-1">
                                                <button
                                                    onClick={() => navigate(`/admin/delivery-staff/${staff._id}`)}
                                                    className="text-indigo-600 hover:text-indigo-900 p-1 rounded-full hover:bg-indigo-50"
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => navigate(`/admin/delivery-staff/edit/${staff._id}`)}
                                                    className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-50"
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleToggleBlock(staff._id, staff.isBlocked)}
                                                    className={`${
                                                        staff.isBlocked
                                                            ? 'text-green-600 hover:text-green-900 hover:bg-green-50'
                                                            : 'text-red-600 hover:text-red-900 hover:bg-red-50'
                                                    } p-1 rounded-full`}
                                                >
                                                    {staff.isBlocked ? (
                                                        <Unlock className="h-4 w-4" />
                                                    ) : (
                                                        <Lock className="h-4 w-4" />
                                                    )}
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(staff._id, staff.name)}
                                                    className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="px-4 py-3 text-center text-xs text-gray-500">
                                        {searchTerm
                                            ? 'No delivery staff found matching your search criteria'
                                            : 'No delivery staff found'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Mobile Staff Cards */}
                <div className="md:hidden p-3">
                    {currentItems.length > 0 ? (
                        currentItems.map((staff) => (
                            <div key={staff._id} className="bg-white rounded-lg shadow-sm p-3 mb-3 border border-gray-100">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                        {staff.profilePicture ? (
                                            <img
                                                className="h-10 w-10 rounded-full object-cover"
                                                src={`${staff.profilePicture}?t=${new Date().getTime()}`}
                                                alt={staff.name}
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = "/images/default_avatar.png";
                                                }}
                                            />
                                        ) : (
                                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                                <User className="h-5 w-5 text-gray-400" />
                                            </div>
                                        )}
                                        <div>
                                            <h3 className="font-medium text-sm text-gray-900">{staff.name}</h3>
                                            <div className="flex items-center text-xs text-gray-500">
                                                ID: {staff.loginId}
                                            </div>
                                        </div>
                                    </div>
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                        staff.isBlocked ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                                    }`}>
                                        {staff.isBlocked ? 'Blocked' : 'Active'}
                                    </span>
                                </div>
                                
                                <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                                    <div>
                                        <span className="text-gray-500">Phone:</span> {staff.phone || 'N/A'}
                                    </div>
                                    <div>
                                        <span className="text-gray-500">Email:</span> {staff.email || 'N/A'}
                                    </div>
                                </div>
                                
                                <div className="mt-2">
                                    <span className="text-xs text-gray-500">Service Areas:</span>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                        {staff.assignedPincodes && staff.assignedPincodes.length > 0 ? (
                                            staff.assignedPincodes.map((pincode, index) => (
                                                <span
                                                    key={`${staff._id}-${pincode}-${index}`}
                                                    className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                                                >
                                                    {pincode}
                                                </span>
                                            ))
                                        ) : (
                                            <span className="text-xs text-gray-500">No areas assigned</span>
                                        )}
                                    </div>
                                </div>
                                
                                <div className="mt-3 flex items-center justify-end space-x-2">
                                    <button
                                        onClick={() => navigate(`/admin/delivery-staff/${staff._id}`)}
                                        className="text-indigo-600 hover:text-indigo-900 p-1.5 rounded-full hover:bg-indigo-50"
                                    >
                                        <Eye className="h-4 w-4" />
                                    </button>
                                    <button
                                        onClick={() => navigate(`/admin/delivery-staff/edit/${staff._id}`)}
                                        className="text-blue-600 hover:text-blue-900 p-1.5 rounded-full hover:bg-blue-50"
                                    >
                                        <Edit className="h-4 w-4" />
                                    </button>
                                    <button
                                        onClick={() => handleToggleBlock(staff._id, staff.isBlocked)}
                                        className={`${
                                            staff.isBlocked
                                                ? 'text-green-600 hover:text-green-900 hover:bg-green-50'
                                                : 'text-red-600 hover:text-red-900 hover:bg-red-50'
                                        } p-1.5 rounded-full`}
                                    >
                                        {staff.isBlocked ? (
                                            <Unlock className="h-4 w-4" />
                                        ) : (
                                            <Lock className="h-4 w-4" />
                                        )}
                                    </button>
                                    <button
                                        onClick={() => handleDelete(staff._id, staff.name)}
                                        className="text-red-600 hover:text-red-900 p-1.5 rounded-full hover:bg-red-50"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-6 text-sm text-gray-500">
                            {searchTerm
                                ? 'No delivery staff found matching your search criteria'
                                : 'No delivery staff found'}
                        </div>
                    )}
                </div>

                {/* Pagination - shown for both desktop and mobile */}
                {filteredStaff.length > itemsPerPage && (
                    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                                <p className="text-xs text-gray-700">
                                    Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{' '}
                                    <span className="font-medium">
                                        {indexOfLastItem > filteredStaff.length ? filteredStaff.length : indexOfLastItem}
                                    </span>{' '}
                                    of <span className="font-medium">{filteredStaff.length}</span> results
                                </p>
                            </div>
                            <div>
                                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                    <button
                                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                        disabled={currentPage === 1}
                                        className="relative inline-flex items-center px-2 py-1 rounded-l-md border border-gray-300 bg-white text-xs font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <span className="sr-only">Previous</span>
                                        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                                    </button>
                                    {[...Array(totalPages)].map((_, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setCurrentPage(index + 1)}
                                            className={`relative inline-flex items-center px-3 py-1 border ${
                                                currentPage === index + 1
                                                    ? 'bg-[#dc2626] text-white border-[#dc2626] z-10'
                                                    : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
                                            } text-xs font-medium`}
                                        >
                                            {index + 1}
                                        </button>
                                    ))}
                                    <button
                                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                        disabled={currentPage === totalPages}
                                        className="relative inline-flex items-center px-2 py-1 rounded-r-md border border-gray-300 bg-white text-xs font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <span className="sr-only">Next</span>
                                        <ChevronRight className="h-4 w-4" aria-hidden="true" />
                                    </button>
                                </nav>
                            </div>
                        </div>

                        {/* Mobile Pagination */}
                        <div className="flex items-center justify-between w-full sm:hidden">
                            <button
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="relative inline-flex items-center px-2 py-1 rounded-md border border-gray-300 bg-white text-xs font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ChevronLeft className="h-4 w-4 mr-1" />
                                Previous
                            </button>
                            <span className="text-xs text-gray-700">
                                Page {currentPage} of {totalPages}
                            </span>
                            <button
                                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="relative inline-flex items-center px-2 py-1 rounded-md border border-gray-300 bg-white text-xs font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Next
                                <ChevronRight className="h-4 w-4 ml-1" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Mobile Optimization Styles */}
            <style jsx>{`
                @media (max-width: 640px) {
                    .text-sm {
                        font-size: 0.75rem;
                        line-height: 1rem;
                    }
                    
                    .p-4 {
                        padding: 0.75rem;
                    }
                    
                    .space-y-4 > :not([hidden]) ~ :not([hidden]) {
                        --tw-space-y-reverse: 0;
                        margin-top: calc(0.75rem * calc(1 - var(--tw-space-y-reverse)));
                        margin-bottom: calc(0.75rem * var(--tw-space-y-reverse));
                    }
                    
                    .py-1\\.5 {
                        padding-top: 0.25rem;
                        padding-bottom: 0.25rem;
                    }
                    
                    .mb-4 {
                        margin-bottom: 0.75rem;
                    }
                    
                    .gap-3 {
                        gap: 0.5rem;
                    }
                    
                    .p-3 {
                        padding: 0.5rem;
                    }
                    
                    .h-10 {
                        height: 2.25rem;
                    }
                    
                    .w-10 {
                        width: 2.25rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default DeliveryStaffList;